import { useEffect } from "react";
import { Link } from "react-router-dom";
import { List, Scale } from "lucide-react";

function Block({ b }) {
  if (b.t === "h") {
    return <h3 className="font-sans font-bold text-navy mt-7 mb-2">{b.x}</h3>;
  }
  if (b.t === "ul") {
    return (
      <ul className="my-3 space-y-1.5">
        {b.items.map((it, i) => (
          <li key={i} className="flex items-start gap-2.5 text-clay leading-relaxed">
            <span className="w-1.5 h-1.5 rounded-full bg-gold mt-2 shrink-0" />
            <span>{it}</span>
          </li>
        ))}
      </ul>
    );
  }
  if (b.t === "link") {
    const cls = "text-navy-royal font-semibold hover:text-gold-dark transition-colors";
    const inner = b.href.startsWith("mailto:") || b.href.startsWith("http") ? (
      <a href={b.href} className={cls} data-testid={b.testid}>{b.x}</a>
    ) : (
      <Link to={b.href} className={cls} data-testid={b.testid}>{b.x}</Link>
    );
    return (
      <p className="my-3 text-clay leading-relaxed">
        {b.label && <span className="font-semibold text-navy">{b.label} </span>}
        {inner}
      </p>
    );
  }
  if (b.t === "motto") {
    return <p className="mt-8 font-serif italic text-xl text-gold-dark">{b.x}</p>;
  }
  return <p className="my-3 text-clay leading-relaxed">{b.x}</p>;
}

export default function LegalPage({ title, docTitle, dateLabel, date, sections, testPrefix }) {
  useEffect(() => {
    document.title = docTitle;
    window.scrollTo(0, 0);
  }, [docTitle]);

  return (
    <div className="bg-cream min-h-screen" data-testid={`${testPrefix}-page`}>
      <section className="bg-navy acheik-bg-dark text-white py-14 lg:py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
          <p className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.25em] text-gold mb-4">
            <Scale className="w-4 h-4" /> Legal
          </p>
          <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight" data-testid={`${testPrefix}-title`}>
            {title}
          </h1>
          <p className="mt-4 text-white/70 text-sm sm:text-base" data-testid={`${testPrefix}-effective-date`}>
            BurmeseHub.com · {dateLabel}: {date}
          </p>
        </div>
      </section>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12 lg:py-16">
        <nav className="bg-white border border-[#E2E8F0] rounded-2xl p-6 sm:p-8 mb-12 shadow-ambient" data-testid={`${testPrefix}-toc`}>
          <p className="flex items-center gap-2 font-sans font-bold text-navy mb-4">
            <List className="w-4 h-4 text-gold-dark" /> On this page
          </p>
          <ol className="grid sm:grid-cols-2 gap-x-8 gap-y-2 list-decimal list-inside">
            {sections.map((s) => (
              <li key={s.id} className="text-sm">
                <a
                  href={`#${s.id}`}
                  data-testid={`${testPrefix}-toc-${s.id}`}
                  className="text-navy-royal hover:text-gold-dark transition-colors font-medium"
                >
                  {s.title}
                </a>
              </li>
            ))}
          </ol>
        </nav>

        <article className="space-y-12">
          {sections.map((s, i) => (
            <section key={s.id} id={s.id} className="scroll-mt-28" data-testid={`${testPrefix}-section-${s.id}`}>
              <h2 className="font-serif text-2xl sm:text-3xl font-bold text-navy tracking-tight pb-3 mb-4 border-b border-[#E2E8F0]">
                <span className="text-gold-dark font-mono text-lg mr-3">{i + 1}.</span>
                {s.title}
              </h2>
              {s.blocks.map((b, j) => <Block key={j} b={b} />)}
            </section>
          ))}
        </article>
      </div>
    </div>
  );
}
